import { useState, useEffect, useRef } from 'react';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { 
  MdVisibility, 
  MdVisibilityOff, 
  MdLock, 
  MdPerson, 
  MdEmail, 
  MdAdminPanelSettings, 
  MdSmartphone, 
  MdPhotoCamera,
  MdStars,
  MdFormatQuote,
  MdAutoAwesome
} from 'react-icons/md';

export default function Settings() {
  const { admin, updateUser } = useAuth();
  const isAdmin = admin?.role === 'admin';

  const [activeTab, setActiveTab] = useState('account');

  // Account password & profile state
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [loading, setLoading] = useState(false);
  const [showFields, setShowFields] = useState({ current: false, newPw: false, confirm: false });
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarInputRef = useRef(null);

  // App version settings state
  const [appVersionsForm, setAppVersionsForm] = useState({
    android: { version_name: '', version_code: '', force_update: false, min_version_code: '', min_version_name: '', store_url: '' },
    ios: { version_name: '', version_code: '', force_update: false, min_version_code: '', min_version_name: '', store_url: '' },
  });
  const [appVersionsLoading, setAppVersionsLoading] = useState(false);

  // Standard Contributor Rate state
  const [standardAmount, setStandardAmount] = useState('');
  const [savingAmount, setSavingAmount] = useState(false);

  // Contributor Daily Article Limit state
  const [dailyLimit, setDailyLimit] = useState(30);
  const [savingDailyLimit, setSavingDailyLimit] = useState(false);

  // Quotes & Ads Feed Position state
  const [feedPositionsForm, setFeedPositionsForm] = useState({
    quote_position: 5,
    quote_interval: 8,
    ad_position: 8,
    ad_interval: 8,
  });
  const [savingFeedPositions, setSavingFeedPositions] = useState(false);

  // AI Check Before Publishing state
  const [aiCheckEnabled, setAiCheckEnabled] = useState(true);
  const [savingAiCheck, setSavingAiCheck] = useState(false);

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/admin/upload/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const imageUrl = res.data?.data?.url;
      if (imageUrl) {
        await api.put('/admin/settings/profile', { image: imageUrl });
        if (updateUser) {
          updateUser({ image: imageUrl });
        }
        toast.success('Profile photo updated successfully!');
      }
    } catch (err) {
      console.error('Failed to upload avatar:', err);
      toast.error('Failed to update profile photo');
    } finally {
      setUploadingAvatar(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchAppVersions();
      fetchStandardAmount();
      fetchDailyLimit();
      fetchFeedPositions();
      fetchAiCheckSetting();
    }
  }, [isAdmin]);

  const fetchAiCheckSetting = async () => {
    try {
      const res = await api.get('/admin/settings/ai-check');
      if (res.data?.success && res.data?.data) {
        setAiCheckEnabled(Boolean(res.data.data.enabled));
      }
    } catch (err) {
      console.error('Failed to fetch AI check setting:', err);
    }
  };

  const handleToggleAiCheck = async (targetValue) => {
    const newValue = targetValue !== undefined ? targetValue : !aiCheckEnabled;
    setSavingAiCheck(true);
    try {
      const res = await api.post('/admin/settings/ai-check', { enabled: newValue });
      if (res.data?.success) {
        setAiCheckEnabled(Boolean(res.data.data?.enabled));
        toast.success(res.data.message || `AI Check is now ${newValue ? 'ON' : 'OFF'}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update AI Check setting');
    } finally {
      setSavingAiCheck(false);
    }
  };

  const fetchAppVersions = async () => {
    try {
      const res = await api.get('/admin/settings/app-version');
      if (res.data?.success && res.data?.data) {
        setAppVersionsForm(res.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch app versions:', err);
    }
  };

  const fetchStandardAmount = async () => {
    try {
      const res = await api.get('/admin/settings/standard-contributor-amount');
      if (res.data?.success && res.data?.data) {
        setStandardAmount(res.data.data.amount ?? 50);
      }
    } catch (err) {
      console.error('Failed to fetch standard contributor rate:', err);
    }
  };

  const fetchDailyLimit = async () => {
    try {
      const res = await api.get('/admin/settings/contributor-daily-limit');
      if (res.data?.success && res.data?.data) {
        setDailyLimit(res.data.data.limit ?? 30);
      }
    } catch (err) {
      console.error('Failed to fetch contributor daily article limit:', err);
    }
  };

  const handleSaveDailyLimit = async (e) => {
    e.preventDefault();
    const limitNum = parseInt(dailyLimit, 10);
    if (isNaN(limitNum) || limitNum <= 0) {
      return toast.error('Daily article limit must be an integer greater than 0');
    }
    setSavingDailyLimit(true);
    try {
      const res = await api.post('/admin/settings/contributor-daily-limit', { limit: limitNum });
      if (res.data?.success) {
        setDailyLimit(res.data.data?.limit ?? limitNum);
        toast.success(res.data.message || 'Contributor daily article limit updated');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update daily article limit');
    } finally {
      setSavingDailyLimit(false);
    }
  };

  const fetchFeedPositions = async () => {
    try {
      const res = await api.get('/admin/settings/feed-positions');
      if (res.data?.success && res.data?.data) {
        setFeedPositionsForm({
          quote_position: res.data.data.quote_position ?? 5,
          quote_interval: res.data.data.quote_interval ?? 8,
          ad_position: res.data.data.ad_position ?? 8,
          ad_interval: res.data.data.ad_interval ?? 8,
        });
      }
    } catch (err) {
      console.error('Failed to fetch feed positions:', err);
    }
  };

  const handleSaveFeedPositions = async (e) => {
    e.preventDefault();
    const qPos = parseInt(feedPositionsForm.quote_position, 10);
    const qInt = parseInt(feedPositionsForm.quote_interval, 10);
    const aPos = parseInt(feedPositionsForm.ad_position, 10);
    const aInt = parseInt(feedPositionsForm.ad_interval, 10);

    if (isNaN(qPos) || qPos < 1) return toast.error('Quote Position must be at least 1');
    if (isNaN(qInt) || qInt < 1) return toast.error('Quote Interval must be at least 1');
    if (isNaN(aPos) || aPos < 1) return toast.error('Ad Position must be at least 1');
    if (isNaN(aInt) || aInt < 1) return toast.error('Ad Interval must be at least 1');

    setSavingFeedPositions(true);
    try {
      const res = await api.post('/admin/settings/feed-positions', {
        quote_position: qPos,
        quote_interval: qInt,
        ad_position: aPos,
        ad_interval: aInt,
      });
      if (res.data?.success) {
        toast.success(res.data.message || 'Quotes & Ads feed position settings updated successfully');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update feed positions');
    } finally {
      setSavingFeedPositions(false);
    }
  };

  const handleSaveStandardAmount = async (e) => {
    e.preventDefault();
    const amt = parseFloat(standardAmount);
    if (isNaN(amt) || amt < 0) {
      return toast.error('Please enter a valid non-negative amount');
    }
    setSavingAmount(true);
    try {
      const res = await api.post('/admin/settings/standard-contributor-amount', { amount: amt });
      if (res.data?.success) {
        toast.success(res.data.message || 'Standard contributor rate updated');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update rate');
    } finally {
      setSavingAmount(false);
    }
  };

  const toggleShow = (field) => setShowFields(prev => ({ ...prev, [field]: !prev[field] }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm_password) {
      return toast.error('Passwords do not match');
    }
    if (form.new_password.length < 6) {
      return toast.error('Password must be at least 6 characters');
    }
    setLoading(true);
    try {
      await api.put('/admin/settings/password', {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success('Password updated successfully');
      setForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleAppVersionsSubmit = async (e) => {
    e.preventDefault();
    if (!isAdmin) {
      return toast.error('Admin access required');
    }

    const hasAndroidName = !!appVersionsForm.android?.version_name?.trim();
    const hasAndroidCode = appVersionsForm.android?.version_code !== '' && appVersionsForm.android?.version_code !== null && appVersionsForm.android?.version_code !== undefined;

    const hasIosName = !!appVersionsForm.ios?.version_name?.trim();
    const hasIosCode = appVersionsForm.ios?.version_code !== '' && appVersionsForm.ios?.version_code !== null && appVersionsForm.ios?.version_code !== undefined;

    if ((hasAndroidName && !hasAndroidCode) || (!hasAndroidName && hasAndroidCode)) {
      return toast.error('Please provide both Latest Version Name and Code for Android');
    }
    if ((hasIosName && !hasIosCode) || (!hasIosName && hasIosCode)) {
      return toast.error('Please provide both Latest Version Name and Code for iOS');
    }

    const hasAndroid = hasAndroidName && hasAndroidCode;
    const hasIos = hasIosName && hasIosCode;

    if (!hasAndroid && !hasIos) {
      return toast.error('Please enter version details for Android or iOS');
    }

    setAppVersionsLoading(true);
    try {
      await api.post('/admin/settings/app-version', {
        android: hasAndroid ? appVersionsForm.android : undefined,
        ios: hasIos ? appVersionsForm.ios : undefined,
      });
      toast.success('App Version settings updated successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update app versions');
    } finally {
      setAppVersionsLoading(false);
    }
  };

  const handleAppVersionChange = (platform, field, value) => {
    setAppVersionsForm(prev => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        [field]: value
      }
    }));
  };

  return (
    <div className="page">
      <div className="page-header">
        <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text)' }}>Admin Settings</h2>
      </div>

      {isAdmin && (
        <div className="settings-nav-tabs">
          <button
            type="button"
            className={`settings-nav-tab ${activeTab === 'account' ? 'active' : ''}`}
            onClick={() => setActiveTab('account')}
          >
            <MdLock /> Account & Security
          </button>
          <button
            type="button"
            className={`settings-nav-tab ${activeTab === 'app_versions' ? 'active' : ''}`}
            onClick={() => setActiveTab('app_versions')}
          >
            <MdSmartphone /> App Versions
          </button>
          <button
            type="button"
            className={`settings-nav-tab ${activeTab === 'contributor_rate' ? 'active' : ''}`}
            onClick={() => setActiveTab('contributor_rate')}
          >
            <MdStars /> Contributor Settings
          </button>
          <button
            type="button"
            className={`settings-nav-tab ${activeTab === 'feed_positions' ? 'active' : ''}`}
            onClick={() => setActiveTab('feed_positions')}
          >
            <MdFormatQuote /> Quotes &amp; Ads Positions
          </button>
          <button
            type="button"
            className={`settings-nav-tab ${activeTab === 'ai_check' ? 'active' : ''}`}
            onClick={() => setActiveTab('ai_check')}
          >
            <MdAutoAwesome /> AI Check
          </button>
        </div>
      )}

      {/* ── TAB 1: Account & Security ── */}
      {activeTab === 'account' && (
        <div className="settings-container">
          <div className="settings-grid" style={!isAdmin ? { maxWidth: 640, margin: '0 auto' } : {}}>
            
            {/* Main Column */}
            <div className="settings-column-main">
              
              {/* Security Card */}
              <div className="settings-card">
                <div className="settings-card-header">
                  <div className="settings-card-title">
                    <MdLock style={{ fontSize: 18, color: 'var(--text)' }} /> Security
                  </div>
                  <div className="settings-card-subtitle">
                    Update your password regularly to keep your account secure.
                  </div>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="settings-input-group">
                    <label className="settings-input-label">Current Password</label>
                    <div className="settings-input-wrapper">
                      <input 
                        type={showFields.current ? 'text' : 'password'} 
                        value={form.current_password}
                        onChange={e => setForm(f => ({ ...f, current_password: e.target.value }))}
                        placeholder="Enter current password"
                        className="settings-input"
                        required 
                      />
                      <button type="button" className="settings-input-icon-btn" onClick={() => toggleShow('current')} tabIndex={-1}>
                        {showFields.current ? <MdVisibilityOff /> : <MdVisibility />}
                      </button>
                    </div>
                  </div>

                  <div className="settings-input-group">
                    <label className="settings-input-label">New Password</label>
                    <div className="settings-input-wrapper">
                      <input 
                        type={showFields.newPw ? 'text' : 'password'} 
                        value={form.new_password}
                        onChange={e => setForm(f => ({ ...f, new_password: e.target.value }))}
                        placeholder="Minimum 6 characters"
                        className="settings-input"
                        required 
                      />
                      <button type="button" className="settings-input-icon-btn" onClick={() => toggleShow('newPw')} tabIndex={-1}>
                        {showFields.newPw ? <MdVisibilityOff /> : <MdVisibility />}
                      </button>
                    </div>
                  </div>

                  <div className="settings-input-group">
                    <label className="settings-input-label">Confirm New Password</label>
                    <div className="settings-input-wrapper">
                      <input 
                        type={showFields.confirm ? 'text' : 'password'} 
                        value={form.confirm_password}
                        onChange={e => setForm(f => ({ ...f, confirm_password: e.target.value }))}
                        placeholder="Confirm your new password"
                        className="settings-input"
                        required 
                      />
                      <button type="button" className="settings-input-icon-btn" onClick={() => toggleShow('confirm')} tabIndex={-1}>
                        {showFields.confirm ? <MdVisibilityOff /> : <MdVisibility />}
                      </button>
                    </div>
                  </div>

                  <button type="submit" className="btn-settings-red" disabled={loading}>
                    {loading ? 'Updating Password...' : 'Update Password'}
                  </button>
                </form>
              </div>

              {/* Security Tip Card */}
              <div className="security-tip-card">
                <div className="security-tip-title">
                  <MdLock style={{ fontSize: 16 }} /> Security Tip
                </div>
                <p className="security-tip-text">
                  Ensure your password uses a mix of uppercase letters, numbers, and symbols for maximum security.
                </p>
              </div>

            </div>

            {/* Profile Side Column */}
            <div className="settings-column-side">
              <div className="profile-card">
                <div className="profile-card-banner">
                  <div 
                    className="profile-avatar-box" 
                    style={{ position: 'relative', overflow: 'hidden', cursor: 'pointer' }}
                    onClick={() => avatarInputRef.current?.click()}
                    title="Click to change profile picture"
                  >
                    {admin?.image ? (
                      <img src={admin.image} alt={admin.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      admin?.name?.charAt(0) || 'A'
                    )}
                    <div className="profile-avatar-overlay">
                      <MdPhotoCamera size={20} />
                    </div>
                  </div>

                  <input 
                    ref={avatarInputRef}
                    type="file" 
                    accept="image/*" 
                    style={{ display: 'none' }} 
                    onChange={handleAvatarUpload} 
                    disabled={uploadingAvatar} 
                  />

                  <button 
                    type="button" 
                    className="profile-photo-btn"
                    onClick={() => avatarInputRef.current?.click()}
                    disabled={uploadingAvatar}
                  >
                    <MdPhotoCamera size={14} />
                    <span>{uploadingAvatar ? 'Uploading...' : (admin?.image ? 'Change Photo' : 'Upload Photo')}</span>
                  </button>

                  <h3 className="profile-title">{admin?.name || 'USER'}</h3>
                  <div className="profile-subtitle">{isAdmin ? 'Administrator' : 'Reporter'}</div>
                </div>

                <div className="profile-card-details">
                  <div className="profile-detail-item">
                    <span className="profile-detail-label"><MdPerson /> FULL NAME</span>
                    <span className="profile-detail-value">{admin?.name || '-'}</span>
                  </div>

                  <div className="profile-detail-item">
                    <span className="profile-detail-label"><MdEmail /> EMAIL ADDRESS</span>
                    <span className="profile-detail-value">{admin?.email || '-'}</span>
                  </div>

                  <div className="profile-detail-item">
                    <span className="profile-detail-label"><MdAdminPanelSettings /> ROLE</span>
                    <span className="profile-role-badge">
                      {isAdmin ? 'Super Admin' : (admin?.role ? admin.role.charAt(0).toUpperCase() + admin.role.slice(1) : 'Reporter')}
                    </span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* ── TAB 2: App Versions ── */}
      {activeTab === 'app_versions' && isAdmin && (
        <div style={{ maxWidth: 720 }}>
          <div className="settings-card">
            <div className="settings-card-header">
              <div className="settings-card-title">
                <MdSmartphone style={{ fontSize: 18, color: 'var(--text)' }} /> App Version Settings
              </div>
              <div className="settings-card-subtitle">
                Manage version requirements for Android and iOS independently.
              </div>
            </div>

            <form onSubmit={handleAppVersionsSubmit}>
              {/* Android Section */}
              <div className="app-version-section-header">Android</div>
              
              <div className="app-version-grid">
                <div className="settings-input-group">
                  <label className="settings-input-label">Latest Version Name</label>
                  <input
                    type="text"
                    value={appVersionsForm.android.version_name}
                    onChange={(e) => handleAppVersionChange('android', 'version_name', e.target.value)}
                    placeholder="e.g. 2.0.3"
                    className="settings-input"
                  />
                </div>
                <div className="settings-input-group">
                  <label className="settings-input-label">Latest Version Code</label>
                  <input
                    type="number"
                    value={appVersionsForm.android.version_code}
                    onChange={(e) => handleAppVersionChange('android', 'version_code', e.target.value === '' ? '' : Number(e.target.value))}
                    placeholder="e.g. 35"
                    className="settings-input"
                  />
                </div>
              </div>

              <div className="app-version-grid">
                <div className="settings-input-group">
                  <label className="settings-input-label">Minimum Version Name (optional)</label>
                  <input
                    type="text"
                    value={appVersionsForm.android.min_version_name}
                    onChange={(e) => handleAppVersionChange('android', 'min_version_name', e.target.value)}
                    placeholder="Leave blank = same as Force Update below"
                    className="settings-input"
                  />
                </div>
                <div className="settings-input-group">
                  <label className="settings-input-label">Minimum Version Code (optional)</label>
                  <input
                    type="number"
                    value={appVersionsForm.android.min_version_code}
                    onChange={(e) => handleAppVersionChange('android', 'min_version_code', e.target.value === '' ? '' : Number(e.target.value))}
                    placeholder="e.g. 28 — below this is force-blocked"
                    className="settings-input"
                  />
                </div>
              </div>

              <div className="settings-input-group">
                <label className="settings-input-label">Play Store URL</label>
                <input
                  type="text"
                  value={appVersionsForm.android.store_url}
                  onChange={(e) => handleAppVersionChange('android', 'store_url', e.target.value)}
                  placeholder="https://play.google.com/store/apps/details?id=..."
                  className="settings-input"
                />
              </div>

              <label className="settings-checkbox-wrapper">
                <span className="settings-checkbox-label">Force Update everything below Latest (only applies when Minimum Version Code above is left blank)</span>
                <input
                  type="checkbox"
                  checked={appVersionsForm.android.force_update}
                  onChange={(e) => handleAppVersionChange('android', 'force_update', e.target.checked)}
                />
              </label>

              {/* iOS Section */}
              <div className="app-version-section-header">iOS</div>

              <div className="app-version-grid">
                <div className="settings-input-group">
                  <label className="settings-input-label">Latest Version Name</label>
                  <input 
                    type="text" 
                    value={appVersionsForm.ios.version_name}
                    onChange={(e) => handleAppVersionChange('ios', 'version_name', e.target.value)}
                    placeholder="e.g. 2.0.0"
                    className="settings-input"
                  />
                </div>
                <div className="settings-input-group">
                  <label className="settings-input-label">Latest Version Code</label>
                  <input
                    type="number"
                    value={appVersionsForm.ios.version_code}
                    onChange={(e) => handleAppVersionChange('ios', 'version_code', e.target.value === '' ? '' : Number(e.target.value))}
                    placeholder="e.g. 2"
                    className="settings-input"
                  />
                </div>
              </div>

              <div className="app-version-grid">
                <div className="settings-input-group">
                  <label className="settings-input-label">Minimum Version Name (optional)</label>
                  <input
                    type="text"
                    value={appVersionsForm.ios.min_version_name}
                    onChange={(e) => handleAppVersionChange('ios', 'min_version_name', e.target.value)}
                    placeholder="Leave blank = same as Force Update below"
                    className="settings-input"
                  />
                </div>
                <div className="settings-input-group">
                  <label className="settings-input-label">Minimum Version Code (optional)</label>
                  <input
                    type="number"
                    value={appVersionsForm.ios.min_version_code}
                    onChange={(e) => handleAppVersionChange('ios', 'min_version_code', e.target.value === '' ? '' : Number(e.target.value))}
                    placeholder="e.g. 1 — below this is force-blocked"
                    className="settings-input"
                  />
                </div>
              </div>

              <div className="settings-input-group">
                <label className="settings-input-label">App Store URL</label>
                <input
                  type="text"
                  value={appVersionsForm.ios.store_url}
                  onChange={(e) => handleAppVersionChange('ios', 'store_url', e.target.value)}
                  placeholder="https://apps.apple.com/app/..."
                  className="settings-input"
                />
              </div>

              <label className="settings-checkbox-wrapper">
                <span className="settings-checkbox-label">Force Update everything below Latest (only applies when Minimum Version Code above is left blank)</span>
                <input
                  type="checkbox"
                  checked={appVersionsForm.ios.force_update}
                  onChange={(e) => handleAppVersionChange('ios', 'force_update', e.target.checked)}
                />
              </label>

              <button type="submit" className="btn-settings-red" disabled={appVersionsLoading}>
                {appVersionsLoading ? 'Saving Settings...' : 'Save Settings'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ── TAB 3: Contributor Settings & Limits ── */}
      {activeTab === 'contributor_rate' && isAdmin && (
        <div style={{ maxWidth: 1080 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '24px', alignItems: 'stretch' }}>
            {/* Card 1: Contributor Daily Article Limit */}
            <div className="settings-card" style={{ marginBottom: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
              <div className="settings-card-header">
                <div className="settings-card-title">
                  <MdStars style={{ fontSize: 18, color: '#EF233C' }} /> Contributor Article Limit Per Day
                </div>
                <div className="settings-card-subtitle">
                  Maximum number of articles a Contributor can submit per day. The system enforces this dynamically.
                </div>
              </div>

              <form onSubmit={handleSaveDailyLimit} style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <div className="settings-input-group">
                  <label className="settings-input-label">Daily Article Limit</label>
                  <div className="settings-input-wrapper" style={{ maxWidth: 320 }}>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={dailyLimit}
                      onChange={(e) => setDailyLimit(e.target.value)}
                      placeholder="30"
                      className="settings-input"
                      required
                    />
                  </div>
                </div>

                <div style={{ marginTop: 16 }}>
                  <button
                    type="submit"
                    className="btn-settings-red"
                    disabled={savingDailyLimit}
                    style={{ width: 'auto', minWidth: 120, padding: '10px 24px' }}
                  >
                    {savingDailyLimit ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            </div>

            {/* Card 2: Standard Contributor Article Rate */}
            <div className="settings-card" style={{ marginBottom: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
              <div className="settings-card-header">
                <div className="settings-card-title">
                  <MdStars style={{ fontSize: 18, color: '#0D8A43' }} /> Standard Contributor Article Rate
                </div>
                <div className="settings-card-subtitle">
                  Configure the fixed earning amount credited to Standard Contributors for each automatically accepted and published article.
                </div>
              </div>

              <form onSubmit={handleSaveStandardAmount} style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <div className="settings-input-group">
                  <label className="settings-input-label">Standard Contributor Amount per Article (₹)</label>
                  <div className="settings-input-wrapper" style={{ maxWidth: 320 }}>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={standardAmount}
                      onChange={(e) => setStandardAmount(e.target.value)}
                      placeholder="50"
                      className="settings-input"
                      required
                    />
                  </div>
                </div>

                <div style={{ marginTop: 16 }}>
                  <button
                    type="submit"
                    className="btn-settings-red"
                    disabled={savingAmount}
                    style={{ width: 'auto', minWidth: 120, padding: '10px 24px' }}
                  >
                    {savingAmount ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ── TAB 4: Quotes & Ads Feed Positions ── */}
      {activeTab === 'feed_positions' && isAdmin && (
        <div style={{ maxWidth: 800 }}>
          <div className="settings-card">
            <div className="settings-card-header">
              <div className="settings-card-title">
                <MdFormatQuote style={{ fontSize: 22, color: '#EF233C' }} /> Quotes &amp; Ads Feed Position Settings
              </div>
              <div className="settings-card-subtitle">
                Configure where Quotes and Fullscreen Ads appear in the mobile app swipe feed.
              </div>
            </div>

            <form onSubmit={handleSaveFeedPositions}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px', marginBottom: '24px' }}>
                
                {/* Quote Settings Box */}
                <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: '12px', padding: '20px' }}>
                  <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '18px' }}>💬</span> Quotes Configuration
                  </div>

                  <div className="settings-input-group" style={{ marginBottom: '16px' }}>
                    <label className="settings-input-label" style={{ fontWeight: '600' }}>Quote Position</label>
                    <div className="settings-input-wrapper">
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={feedPositionsForm.quote_position}
                        onChange={(e) => setFeedPositionsForm(prev => ({ ...prev, quote_position: e.target.value }))}
                        placeholder="e.g. 6"
                        className="settings-input"
                        required
                      />
                    </div>
                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px', display: 'block' }}>
                      Defines the position at which the first Quote appears in the app feed.
                    </span>
                  </div>

                  <div className="settings-input-group">
                    <label className="settings-input-label" style={{ fontWeight: '600' }}>Quote Interval</label>
                    <div className="settings-input-wrapper">
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={feedPositionsForm.quote_interval}
                        onChange={(e) => setFeedPositionsForm(prev => ({ ...prev, quote_interval: e.target.value }))}
                        placeholder="e.g. 10"
                        className="settings-input"
                        required
                      />
                    </div>
                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px', display: 'block' }}>
                      Defines the interval between subsequent Quote positions.
                    </span>
                  </div>
                </div>

                {/* Ad Settings Box */}
                <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: '12px', padding: '20px' }}>
                  <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '18px' }}>📢</span> Ads Configuration
                  </div>

                  <div className="settings-input-group" style={{ marginBottom: '16px' }}>
                    <label className="settings-input-label" style={{ fontWeight: '600' }}>Ad Position</label>
                    <div className="settings-input-wrapper">
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={feedPositionsForm.ad_position}
                        onChange={(e) => setFeedPositionsForm(prev => ({ ...prev, ad_position: e.target.value }))}
                        placeholder="e.g. 8"
                        className="settings-input"
                        required
                      />
                    </div>
                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px', display: 'block' }}>
                      Defines the position at which the first Ad appears in the app feed.
                    </span>
                  </div>

                  <div className="settings-input-group">
                    <label className="settings-input-label" style={{ fontWeight: '600' }}>Ad Interval</label>
                    <div className="settings-input-wrapper">
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={feedPositionsForm.ad_interval}
                        onChange={(e) => setFeedPositionsForm(prev => ({ ...prev, ad_interval: e.target.value }))}
                        placeholder="e.g. 8"
                        className="settings-input"
                        required
                      />
                    </div>
                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px', display: 'block' }}>
                      Defines the interval between subsequent Ad positions.
                    </span>
                  </div>
                </div>

              </div>

              {/* Dynamic Live Sequence Preview */}
              <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: '10px', padding: '16px 20px', marginBottom: '22px' }}>
                <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text)', marginBottom: '8px' }}>
                  ℹ️ Live Sequence Preview
                </div>
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                  <div>
                    <strong style={{ color: 'var(--text)' }}>Quotes:</strong>{' '}
                    {(() => {
                      const qP = parseInt(feedPositionsForm.quote_position, 10) || 5;
                      const qI = parseInt(feedPositionsForm.quote_interval, 10) || 8;
                      return `${qP}, ${qP + qI}, ${qP + qI * 2}, ${qP + qI * 3}, ${qP + qI * 4}...`;
                    })()}
                  </div>
                  <div>
                    <strong style={{ color: 'var(--text)' }}>Ads:</strong>{' '}
                    {(() => {
                      const aP = parseInt(feedPositionsForm.ad_position, 10) || 8;
                      const aI = parseInt(feedPositionsForm.ad_interval, 10) || 8;
                      return `${aP}, ${aP + aI}, ${aP + aI * 2}, ${aP + aI * 3}, ${aP + aI * 4}...`;
                    })()}
                  </div>
                </div>
              </div>

              <button
                type="submit"
                className="btn-settings-red"
                disabled={savingFeedPositions}
                style={{ width: 'auto', minWidth: 140, padding: '10px 28px' }}
              >
                {savingFeedPositions ? 'Saving...' : 'Save Settings'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ── TAB 5: AI Check ── */}
      {activeTab === 'ai_check' && (
        <div className="settings-container">
          <div className="settings-card" style={{ maxWidth: 780 }}>
            <div className="settings-card-header">
              <div className="settings-card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <MdAutoAwesome style={{ fontSize: 22, color: 'var(--primary, #E11D48)' }} /> AI Check Before Article Publishing
              </div>
              <div className="settings-card-subtitle">
                Global control to enable or disable automatic AI checks before articles are published. Applies to both Normal and Zonal articles.
              </div>
            </div>

            <div style={{ marginTop: '20px' }}>
              {/* Main Switch Box */}
              <div style={{
                background: 'var(--bg)',
                border: `1.5px solid ${aiCheckEnabled ? '#10B981' : '#F59E0B'}`,
                borderRadius: '14px',
                padding: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '20px',
                marginBottom: '24px',
                transition: 'all 0.2s ease'
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                    <span style={{ fontSize: '18px', fontWeight: '800', color: 'var(--text)' }}>
                      AI Check Status:
                    </span>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '4px 12px',
                      borderRadius: '20px',
                      fontSize: '13px',
                      fontWeight: '800',
                      letterSpacing: '0.5px',
                      background: aiCheckEnabled ? '#DCFCE7' : '#FEE2E2',
                      color: aiCheckEnabled ? '#166534' : '#991B1B'
                    }}>
                      <span style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: aiCheckEnabled ? '#16A34A' : '#DC2626'
                      }} />
                      {aiCheckEnabled ? 'ON (ENABLED)' : 'OFF (DISABLED)'}
                    </span>
                  </div>
                  <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                    {aiCheckEnabled
                      ? 'AI checks are currently active. Articles will undergo reader comprehension, editorial review, and duplicate checks before publishing.'
                      : 'AI checks are currently disabled. Normal and Zonal articles will publish immediately without calling AI services.'}
                  </p>
                </div>

                {/* Switch Toggle Button */}
                <button
                  type="button"
                  onClick={() => handleToggleAiCheck(!aiCheckEnabled)}
                  disabled={savingAiCheck}
                  style={{
                    position: 'relative',
                    width: '68px',
                    height: '36px',
                    borderRadius: '18px',
                    border: 'none',
                    background: aiCheckEnabled ? '#10B981' : '#9CA3AF',
                    cursor: savingAiCheck ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.25s ease',
                    padding: '3px',
                    display: 'flex',
                    alignItems: 'center',
                    outline: 'none',
                    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)'
                  }}
                  title={`Click to turn AI Check ${aiCheckEnabled ? 'OFF' : 'ON'}`}
                >
                  <span style={{
                    width: '30px',
                    height: '30px',
                    borderRadius: '50%',
                    background: '#FFFFFF',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    transform: aiCheckEnabled ? 'translateX(32px)' : 'translateX(0px)',
                    transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '11px',
                    fontWeight: '800',
                    color: aiCheckEnabled ? '#10B981' : '#6B7280'
                  }}>
                    {aiCheckEnabled ? 'ON' : 'OFF'}
                  </span>
                </button>
              </div>

              {/* Status Mode Explanations */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                <div style={{
                  background: 'var(--bg)',
                  border: '1px solid var(--border)',
                  borderRadius: '12px',
                  padding: '16px 18px',
                  opacity: aiCheckEnabled ? 1 : 0.65
                }}>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text)', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ color: '#10B981' }}>●</span> When ON (Normal Mode)
                  </div>
                  <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                    <li><strong>Normal articles:</strong> Undergo AI reader comprehension &amp; guideline review before publish.</li>
                    <li><strong>Zonal articles:</strong> Undergo AI reader comprehension &amp; guideline review before publish.</li>
                    <li>Automatic AI duplicate gate check runs in background.</li>
                  </ul>
                </div>

                <div style={{
                  background: 'var(--bg)',
                  border: '1px solid var(--border)',
                  borderRadius: '12px',
                  padding: '16px 18px',
                  opacity: !aiCheckEnabled ? 1 : 0.65
                }}>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text)', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ color: '#F59E0B' }}>●</span> When OFF (Fallback Mode)
                  </div>
                  <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                    <li><strong>Skips all AI checks:</strong> Articles publish directly through existing workflow.</li>
                    <li><strong>Zero AI API calls:</strong> Unaffected if AI API key is invalid, expired, or down.</li>
                    <li>Publishing is instant and guaranteed never to fail due to AI issues.</li>
                  </ul>
                </div>
              </div>

              {/* Explicit Action Button */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <button
                  type="button"
                  className="btn-settings-red"
                  onClick={() => handleToggleAiCheck(!aiCheckEnabled)}
                  disabled={savingAiCheck}
                  style={{ width: 'auto', minWidth: 170, padding: '10px 24px' }}
                >
                  {savingAiCheck ? 'Saving...' : `Switch to AI Check ${aiCheckEnabled ? 'OFF' : 'ON'}`}
                </button>
                <span style={{ fontSize: '12.5px', color: 'var(--text-secondary)' }}>
                  Settings take effect immediately for all Normal and Zonal article publishing.
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
